import { useEffect, useMemo, useRef, useState } from 'react';

export interface MultiSelectItem {
  id: string;
  primary: string;
  secondary?: string;
}

interface Props {
  label: string;
  hint?: string;
  items: MultiSelectItem[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  emptyMessage: string;
  error?: string;
}

/**
 * Searchable multi-select with chips. Handles both small and large lists.
 * - Chips above show selected items (click × to remove).
 * - Typing filters available items; arrow keys navigate, Enter selects.
 * - Backspace on empty input removes the last chip.
 */
const MultiSelect = ({
  label,
  hint,
  items,
  selected,
  onChange,
  placeholder = 'Search...',
  emptyMessage,
  error,
}: Props) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const itemById = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);

  const available = useMemo(() => {
    const q = query.trim().toLowerCase();
    const unselected = items.filter((i) => !selected.includes(i.id));
    if (!q) return unselected;
    return unselected.filter(
      (i) =>
        i.primary.toLowerCase().includes(q) || (i.secondary?.toLowerCase().includes(q) ?? false)
    );
  }, [items, selected, query]);

  useEffect(() => {
    setHighlightIdx(0);
  }, [query, available.length]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const add = (id: string) => {
    if (!selected.includes(id)) onChange([...selected, id]);
    setQuery('');
    inputRef.current?.focus();
  };
  const remove = (id: string) => {
    onChange(selected.filter((x) => x !== id));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlightIdx((i) => Math.min(i + 1, available.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (available.length > 0 && open) {
        e.preventDefault();
        add(available[highlightIdx].id);
      }
    } else if (e.key === 'Backspace' && query === '' && selected.length > 0) {
      remove(selected[selected.length - 1]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const hasNoItemsAtAll = items.length === 0;

  return (
    <div className="flex flex-col w-full" ref={containerRef}>
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-14s font-normal text-grey-600">{label}</span>
        {hint && <span className="text-12s text-grey-600">{hint}</span>}
      </div>

      {hasNoItemsAtAll ? (
        <div className="border border-grey-100 rounded-lg px-4 py-3 text-14s text-grey-600 bg-grey-50">
          {emptyMessage}
        </div>
      ) : (
        <div className="relative">
          <div
            className={`flex flex-wrap items-center gap-2 border rounded-lg px-2 py-1.5 min-h-[42px] cursor-text ${
              open ? 'border-primary-500' : 'border-grey-100'
            }`}
            onClick={() => {
              setOpen(true);
              inputRef.current?.focus();
            }}
          >
            {selected.map((id) => {
              const item = itemById.get(id);
              if (!item) return null;
              return (
                <span
                  key={id}
                  className="inline-flex items-center gap-1 bg-primary-500/10 text-primary-500 text-12s rounded px-2 py-0.5"
                >
                  {item.primary}
                  <button
                    type="button"
                    aria-label={`Remove ${item.primary}`}
                    className="text-primary-500/70 hover:text-primary-500 leading-none"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(id);
                    }}
                  >
                    ×
                  </button>
                </span>
              );
            })}
            <input
              ref={inputRef}
              type="text"
              value={query}
              placeholder={selected.length === 0 ? placeholder : ''}
              className="flex-1 min-w-[120px] bg-transparent outline-none text-14s text-grey-900 px-1 py-0.5"
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
            />
          </div>

          {open && (
            <div className="absolute z-10 mt-1 w-full bg-white border border-grey-100 rounded-lg shadow-lg max-h-[220px] overflow-y-auto">
              {available.length === 0 ? (
                <div className="px-3 py-2 text-14s text-grey-600">
                  {query ? `No matches for "${query}"` : 'All available items selected'}
                </div>
              ) : (
                available.map((item, idx) => (
                  <button
                    key={item.id}
                    type="button"
                    className={`w-full text-left px-3 py-2 flex flex-col ${
                      idx === highlightIdx ? 'bg-grey-50' : 'bg-white'
                    } hover:bg-grey-50`}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    onClick={() => add(item.id)}
                  >
                    <span className="text-14s text-grey-900">{item.primary}</span>
                    {item.secondary && (
                      <span className="text-12s text-grey-600">{item.secondary}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {error && <span className="text-red-500 text-xs mt-1">{error}</span>}
    </div>
  );
};

export default MultiSelect;
